/**
 * Composable to define which locales are available for the current page
 * This controls which locales appear in hreflang and og:locale:alternate meta tags
 *
 * @example
 * // Static locale restriction (circolari page - Italian only)
 * definePageLocales(['it']);
 *
 * @example
 * // Dynamic locale restriction (news index - based on available news)
 * const localesWithNews = ['es', 'en', 'it'];
 * definePageLocales(localesWithNews);
 *
 * @example
 * // Dynamic locale restriction (news post - based on post slugs)
 * const availableLocales = Object.keys(post.slugs)
 *   .filter(locale => post.slugs[locale]?.slug)
 *   .map(mapCmsLocaleToAppLocale);
 * definePageLocales(availableLocales);
 */

import { useState } from "#imports";

export const definePageLocales = (locales: string[]): void => {
  // Update useState for SSR and plugin meta generation
  const pageLocales = useState<string[] | null>("page-available-locales");
  pageLocales.value = locales;
};

/**
 * Gets the currently configured available locales for the page
 * Returns null if not set (meaning all locales are available)
 */
export const getPageLocales = (): string[] | null => {
  const pageLocales = useState<string[] | null>("page-available-locales", () => null);
  return pageLocales.value;
};

/**
 * Resets page locales to default (all locales available)
 */
export const resetPageLocales = (): void => {
  const pageLocales = useState<string[] | null>("page-available-locales");
  pageLocales.value = null;
};

/**
 * Maps CMS locale codes to app locale codes
 * CMS uses "pt-br" while app uses "pt"
 */
export const mapCmsLocaleToAppLocale = (cmsLocale: string): string => {
  if (cmsLocale === "pt-br") return "pt";
  return cmsLocale;
};
